import { useMutation, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '@/lib/queryKeys'

import { setDefaultAddress } from '../addressService'
import type { Address } from '../types/Address'

export const useSetDefaultAddress = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (addressId: string) => setDefaultAddress(addressId),
    onMutate: async addressId => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })

      // Snapshot the previous value
      const previousAddresses = queryClient.getQueryData<Address[]>([QUERY_KEYS.ADDRESSES])

      // Optimistically update to the new value
      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES], old =>
        old?.map(addr => ({
          ...addr,
          isDefault: addr.id === addressId,
        }))
      )

      // Return a context object with the snapshotted value
      return { previousAddresses }
    },
    onError: (_error, _addressId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousAddresses) {
        queryClient.setQueryData([QUERY_KEYS.ADDRESSES], context.previousAddresses)
      }
    },
    onSuccess: updatedAddress => {
      // Replace optimistic update with real one from server
      queryClient.setQueryData<Address[]>([QUERY_KEYS.ADDRESSES], old =>
        old?.map(addr => ({
          ...addr,
          isDefault: addr.id === updatedAddress.id,
        }))
      )
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADDRESSES] })
    },
  })
}
